import {
  type NdcGermlineConfigRequest,
  NdcSpreadsheetComponentFormatType,
  type TaggedChainKeyDict,
  type TaggedDomainKeyDict,
  type TaggedVariablePairKeyDict,
  type TranslationParameters,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import {type TargetFile} from 'folder-items/FileConstants';
import {type RegistryLabelingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';

export enum ProteinCreationCellValueType {
  EMPTY = 'empty',
  RAW_TEXT = 'raw_text',
  EXISTING_ENTITY = 'existing_entity',
  BOOLEAN = 'boolean',
  NUMBER = 'number',
  ENTITY_LINK = 'entity_link',
  DROPDOWN_OPTION = 'dropdown_option',
}

export type ProteinCreationLinkedEntity = TargetFile;

export type ProteinCreationCellValue =
  | {type: ProteinCreationCellValueType.EMPTY}
  | {type: ProteinCreationCellValueType.RAW_TEXT; text: string}
  | {type: ProteinCreationCellValueType.EXISTING_ENTITY; entityId: string; displayName: string}
  | {type: ProteinCreationCellValueType.BOOLEAN; value: boolean}
  /** An integer or float schema field value. `text` is the user's input, preserved for display. */
  | {type: ProteinCreationCellValueType.NUMBER; value: number; text: string}
  /** An entity link schema field value. Holds multiple entities for multi-select link fields. */
  | {type: ProteinCreationCellValueType.ENTITY_LINK; entities: Array<ProteinCreationLinkedEntity>}
  /** A dropdown schema field value. `name` is the option's name, kept for display. */
  | {type: ProteinCreationCellValueType.DROPDOWN_OPTION; optionId: string; name: string};

export interface ProteinCreationRow {
  cid: string;
  /** Empty unless the naming strategy takes a name. */
  name: string;
  /** Keyed by component label (e.g. "ELEMENTA(Heavy)", "VH-VL pair"). */
  nestedComponents: Record<string, ProteinCreationCellValue>;
  /** Keyed by schema field API ID (e.g. "tsf_abc123"). */
  schemaFields: Record<string, ProteinCreationCellValue>;
  /** Empty when the row has no alias. */
  alias: string;
}

export interface NewProteinComponentConfigDict {
  aaPairSchemaId: string | null;
  dnaDomainSchemaId: string | null;
  dnaChainSchemaId: string | null;
  dnaPairSchemaId: string | null;
}

// --- Table request types (shared by validation and submission) ---

export type ComponentInputValue =
  | {type: 'RAW_TEXT'; text: string}
  | {type: 'EXISTING_ENTITY'; entityId: string};

export type ComponentFormatKey = TaggedDomainKeyDict | TaggedChainKeyDict | TaggedVariablePairKeyDict;

export interface NestedComponentInput {
  componentFormatKey: ComponentFormatKey;
  value: ComponentInputValue;
}

export type SchemaFieldInputValue =
  | {type: 'RAW_TEXT'; text: string}
  | {type: 'EXISTING_ENTITIES'; entityIds: Array<string>}
  | {type: 'BOOLEAN'; value: boolean}
  | {type: 'DROPDOWN_OPTION'; optionId: string};

export interface ProteinCreationInputRow {
  nestedComponents: Array<NestedComponentInput>;
  /** Keyed by schema field API ID. Fields without a value are omitted. */
  schemaFields: Record<string, SchemaFieldInputValue>;
  /** Omitted when blank. */
  name?: string;
  /** Omitted when the row has no alias. */
  alias?: string;
}

export interface ProteinCreationTableRequest {
  folderId: string;
  proteinSchemaId: string;
  componentFormatType: NdcSpreadsheetComponentFormatType;
  rows: Array<ProteinCreationInputRow>;
  newComponentConfig: NewProteinComponentConfigDict;
  translationParameters: TranslationParameters;
  germlineConfigs: Array<NdcGermlineConfigRequest>;
  entryId: string | null;
  /** When null, the server infers the naming strategy from the schema. */
  namingStrategy: RegistryLabelingStrategy | null;
}
